import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, Search } from 'lucide-react';
import { adminAuditoriaService } from '../../shared/services';

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR');
}

export function AdminAuditoriaPage() {
  const [usuario, setUsuario] = useState('');
  const [metodo, setMetodo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-auditoria', usuario, metodo, page],
    queryFn: () => adminAuditoriaService.listar({ usuario, metodo, page, pageSize }),
  });

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <History size={30} /> Auditoria
          </h1>
          <p className="text-gray-600 mt-1">Registro das operações de escrita realizadas na API.</p>
        </div>

        <div className="card mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
            <input
              value={usuario}
              onChange={(event) => {
                setUsuario(event.target.value);
                setPage(1);
              }}
              placeholder="E-mail do usuário"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2"
            />
          </label>
          <select
            value={metodo}
            onChange={(event) => {
              setMetodo(event.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Todos os métodos</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : isError || !data ? (
          <div className="card text-center py-10 text-red-600">Não foi possível carregar a auditoria.</div>
        ) : (
          <section className="card overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Data</th>
                  <th className="py-2 pr-4">Usuário</th>
                  <th className="py-2 pr-4">Método</th>
                  <th className="py-2 pr-4">Rota</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{formatarData(item.ocorridoEm)}</td>
                    <td className="py-2 pr-4 text-gray-700">{item.usuarioEmail ?? 'anônimo'}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{item.metodo}</td>
                    <td className="py-2 pr-4 text-gray-700 font-mono text-xs">{item.caminho}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.statusCode < 300 ? 'bg-green-100 text-green-800' : item.statusCode < 400 ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.statusCode}
                      </span>
                    </td>
                    <td className="py-2 text-gray-500 text-xs">{item.ip ?? '—'}</td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">Nenhum registro encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>{data.total} registro(s)</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPage((valor) => Math.max(1, valor - 1))} disabled={page <= 1} className="btn-secondary disabled:opacity-50">
                  Anterior
                </button>
                <span>Página {page} de {totalPaginas}</span>
                <button type="button" onClick={() => setPage((valor) => Math.min(totalPaginas, valor + 1))} disabled={page >= totalPaginas} className="btn-secondary disabled:opacity-50">
                  Próxima
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
